import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Gift, Users, Sparkles, ArrowRight, Share2, BadgeCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const STEPS = [
  { icon: Share2, title: "Share your link", body: "Create a free account and get your personal invite link. Post it anywhere — text, social, your group chats." },
  { icon: Users, title: "They join & subscribe", body: "When people you invite become paying Founding Members, they're counted toward your rewards." },
  { icon: Gift, title: "You earn free membership", body: "Every 2 paying members you bring in = 1 free month. Refer 15 in a single month and you unlock a full free year." },
];

export default function AffiliatePage() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";
  const ctaLabel = user ? "Go to your affiliate dashboard" : "Join as an affiliate — free";

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet>
        <title>Affiliate Program — Earn Free Membership · Help Solve The Case™</title>
        <meta name="description" content="Invite friends to Help Solve The Case and earn free membership. Every 2 paying members you refer = 1 free month, automatically." />
        <link rel="canonical" href="https://helpsolvethecase.com/affiliate" />
        <meta property="og:title" content="Earn Free Membership — Help Solve The Case Affiliate Program" />
        <meta property="og:description" content="Every 2 paying members you refer = 1 free month. Free to join." />
      </Helmet>
      <Navbar />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-10 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/30 bg-gold/5 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-gold" />
            <span className="label-mono text-gold">Affiliate Program</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]">
            Bring people in.<br /><span className="text-gold">Ride free.</span>
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-2xl mx-auto leading-relaxed">
            Love the Bullpen? Share it. It's a <span className="text-white font-semibold">free affiliate account</span> — not a paid membership. For every <span className="text-white font-semibold">2 people</span> you invite who become paying Founding Members, you earn a <span className="text-gold font-semibold">free month</span> — automatically.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to={ctaTo} data-testid="affiliate-cta-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </Link>
            {!user && (
              <Link to="/login" className="text-sm text-white/50 hover:text-gold transition-colors">Already have an account? Sign in</Link>
            )}
          </div>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <div className="grid sm:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
              className="card-surface rounded-md p-6" data-testid={`affiliate-step-${i}`}>
              <div className="w-10 h-10 rounded-md bg-gold/10 flex items-center justify-center mb-4"><s.icon className="w-5 h-5 text-gold" /></div>
              <div className="label-mono text-white/40 mb-1">Step {i + 1}</div>
              <h3 className="font-display text-lg font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-white/50 leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 card-surface rounded-md p-6 sm:p-8">
          <div className="flex items-center gap-2 mb-4"><BadgeCheck className="w-4 h-4 text-gold" /><h2 className="label-mono">How the reward works</h2></div>
          <ul className="space-y-2 text-sm text-white/60 leading-relaxed">
            <li>• <span className="text-white">2 paying referrals = 1 free month.</span> Keep going and it adds up automatically.</li>
            <li>• <span className="text-gold font-semibold">Fast track:</span> refer <span className="text-white">15 paying members within a single month</span> and jump straight to a <span className="text-gold font-semibold">full free year</span>.</li>
            <li>• If you're on a <span className="text-white">free account</span>, your earned months unlock full membership at no charge — you can earn your way to a paid-level account entirely through referrals.</li>
            <li>• If you already pay, your free months come off your next bill automatically.</li>
            <li>• Only people who actually <span className="text-white">subscribe</span> count — so it's real support for the work.</li>
          </ul>
          <Link to={ctaTo} data-testid="affiliate-cta-btn-2"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors">
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
